import { isEmpty } from '@sgme/fp';
import classNames from 'classnames';
import { useOnKeyDownWithCanEdit } from 'components/common-form/builder/form/node/field/Grid/cells/hooks';
import { useSPIntl } from 'hooks/useIntl';
import { first, last } from 'lodash-es';
import type { CellMetadata, FieldMetadata } from 'models/forms/fields';
import { FC, MouseEvent, useCallback } from 'react';
import { isNumber } from 'utils/object/object';

import type { Navigation } from '../GridField.model';
import { CustomInputGroupPrepend, CustomizableInput } from './CustomizableInput';
import { ReadOnlyLabel } from './SelectableLabel';
import { doesKeyLooksLikeValidInputForComponent } from './utils';
import { WithCurrency } from './WithCurrency';

function getDateListLabel(datesValues: string[] | undefined, formatToDisplay: (d: string) => string) {
  if (isEmpty(datesValues)) {
    return '';
  }
  const begin = first(datesValues);
  const end = datesValues.length >= 2 ? last(datesValues) : undefined;
  const formatNullableDate = (date: string | undefined) => (date !== undefined ? formatToDisplay(date) : '');
  return `${datesValues.length} selected\t (${formatNullableDate(begin)}...\t${formatNullableDate(end)})`;
}
interface Props {
  cellId: string;
  e2e: string;
  isSelected: boolean;
  metaData: FieldMetadata;
  value: unknown;
  isDraggable: boolean;
  customizedData?: CellMetadata;
  onCellSelected: (cellId: string) => void;
  onEditionStarted: (lostInputKey?: KeyboardEventInit) => void;
  onNavigated: (navigation: Navigation) => void;
}

export const SelectableGridCell: FC<Props> = ({
  cellId,
  e2e,
  isSelected,
  metaData: metadata,
  value = null,
  isDraggable,
  customizedData,
  onCellSelected,
  onEditionStarted,
  onNavigated,
}) => {
  const { formatDisplayedDate } = useSPIntl();

  const onDoubleClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      onEditionStarted();
      e.preventDefault();
    },
    [onEditionStarted],
  );

  const onClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      onCellSelected(cellId);
      e.preventDefault();
    },
    [cellId, onCellSelected],
  );

  const { isReadOnly, type: componentType } = metadata;
  const isCustomized = customizedData?.isCustomized ?? false;

  const onKeyDownWithCanEdit = useOnKeyDownWithCanEdit(onNavigated, onEditionStarted, key =>
    doesKeyLooksLikeValidInputForComponent(key, componentType),
  );
  const onKeyDownEditable = onKeyDownWithCanEdit(!isReadOnly);
  const onKeyDownReadonly = onKeyDownWithCanEdit(false);

  switch (metadata.type) {
    case 'PercentField': {
      return (
        <div className="input-group">
          {isCustomized && <CustomInputGroupPrepend />}

          <ReadOnlyLabel
            id={cellId}
            e2e={e2e}
            value={value}
            onDoubleClick={onDoubleClick}
            onClick={onClick}
            onKeyDown={onKeyDownEditable}
            isSelected={isSelected}
            isReadOnly={isReadOnly}
            isCustomized={isCustomized}
          />
          <span
            className={classNames('unit input-group-text', {
              'text-info': isCustomized,
              'bg-info bg-opacity-20': isDraggable && isSelected,
              'bg-transparent': isDraggable && !isSelected,
            })}
            data-e2e="unit"
          >
            %
          </span>
        </div>
      );
    }
    case 'DateField': {
      return (
        <CustomizableInput isCustomized={isCustomized}>
          <ReadOnlyLabel
            id={cellId}
            e2e={e2e}
            value={value}
            onDoubleClick={onDoubleClick}
            onClick={onClick}
            onKeyDown={onKeyDownEditable}
            isSelected={isSelected}
            isReadOnly={isReadOnly}
            isCustomized={isCustomized}
            formatValue={v => formatDisplayedDate(v)}
          />
        </CustomizableInput>
      );
    }
    case 'AmountField':
      return (
        <WithCurrency currency={metadata.currency} isCustomized={isCustomized}>
          <ReadOnlyLabel
            id={cellId}
            e2e={e2e}
            value={value}
            onDoubleClick={onDoubleClick}
            onClick={onClick}
            onKeyDown={onKeyDownEditable}
            isSelected={isSelected}
            isReadOnly={isReadOnly}
            isCustomized={isCustomized}
          />
        </WithCurrency>
      );
    case 'NumberField':
      const formattedValue: string =
        isNumber(value) && isNumber(metadata.nbDecimals) && metadata.decimalPadding === true
          ? value.toLocaleString(undefined, { minimumFractionDigits: metadata.nbDecimals })
          : (value as string);
      return (
        <CustomizableInput isCustomized={isCustomized}>
          <ReadOnlyLabel
            id={cellId}
            e2e={e2e}
            value={formattedValue}
            onDoubleClick={onDoubleClick}
            onClick={onClick}
            onKeyDown={onKeyDownEditable}
            isSelected={isSelected}
            isReadOnly={isReadOnly}
            isCustomized={isCustomized}
          />
        </CustomizableInput>
      );
    case 'TextField':
      return (
        <CustomizableInput isCustomized={isCustomized}>
          <ReadOnlyLabel
            id={cellId}
            e2e={e2e}
            value={value}
            onDoubleClick={onDoubleClick}
            onClick={onClick}
            onKeyDown={onKeyDownEditable}
            isSelected={isSelected}
            isReadOnly={isReadOnly}
            isCustomized={isCustomized}
          />
        </CustomizableInput>
      );
    case 'DatesListField':
      const datesValues = value as string[] | undefined;
      const label = getDateListLabel(datesValues, formatDisplayedDate);
      return (
        <CustomizableInput isCustomized={isCustomized}>
          <ReadOnlyLabel
            id={cellId}
            e2e={e2e}
            value={label}
            onDoubleClick={onDoubleClick}
            onClick={onClick}
            onKeyDown={onKeyDownEditable}
            isSelected={isSelected}
            isReadOnly={isReadOnly}
            isCustomized={isCustomized}
          />
        </CustomizableInput>
      );
    default: {
      return (
        <CustomizableInput isCustomized={isCustomized}>
          <ReadOnlyLabel
            id={cellId}
            e2e={e2e}
            value={value}
            onDoubleClick={onDoubleClick}
            onClick={onClick}
            onKeyDown={onKeyDownReadonly}
            isSelected={isSelected}
            isReadOnly
            isCustomized={isCustomized}
          />
        </CustomizableInput>
      );
    }
  }
};
SelectableGridCell.displayName = 'ReadOnlyGridCell';
